import api from "@/services/api";

/**
 * @description Servicio para interactuar con el endpoint /urgency-queue.
 * Gestiona la cola de pacientes de urgencias: consulta, toma y completado.
 */
const urgencyQueueService = {};

const endpoint = "/urgency-queue";

/**
 * @description Obtiene la cola de pacientes para una especialidad
 * @param {string} specialtyId - ID de la especialidad de urgencia
 * @returns {Promise<Array>} Lista de pacientes en cola
 */
urgencyQueueService.getBySpecialty = function (specialtyId) {
  return api.get(`${endpoint}/specialty/${specialtyId}`).then((r) => r.data);
};

/**
 * @description Toma un paciente de la cola (el médico lo atiende)
 * @param {string} queueId - ID del item en cola
 * @returns {Promise<Object>} Resultado con datos del paciente y cola actualizada
 */
urgencyQueueService.take = function (queueId) {
  return api.put(`${endpoint}/${queueId}/take`).then((r) => r.data);
};

/**
 * @description Marca un paciente como atendido (completa la atención)
 * @param {string} queueId - ID del item en cola
 * @returns {Promise<Object>} Cola actualizada
 */
urgencyQueueService.complete = function (queueId) {
  return api.put(`${endpoint}/${queueId}/complete`).then((r) => r.data);
};

/**
 * @description Obtiene todas las colas (para admin)
 * @param {Object} [params] - Parámetros de filtro
 * @returns {Promise<Array>} Lista completa de colas
 */
urgencyQueueService.getAll = function (params) {
  return api.get(endpoint, { params }).then((r) => r.data);
};

/**
 * @description Actualiza la prioridad de un paciente en la cola
 * @param {string} queueId - ID del item en cola
 * @param {number} priority - Nueva prioridad
 * @returns {Promise<Object>} Cola actualizada
 */
urgencyQueueService.updatePriority = function (queueId, priority) {
  return api
    .put(`${endpoint}/${queueId}/priority`, { priority })
    .then((r) => r.data);
};

/**
 * @description Busca pacientes por texto libre (nombre, DNI, email)
 * @param {string} q - Texto de búsqueda
 * @returns {Promise<Array>} Lista de pacientes que coinciden
 */
urgencyQueueService.searchPatient = function (q) {
  return api
    .get(`${endpoint}/search-patient`, { params: { q } })
    .then((r) => r.data);
};

/**
 * @description Crea un caso de urgencia desde admin/operador
 * @param {Object} data - Datos del caso
 * @param {string} [data.patientId] - ID del paciente existente
 * @param {Object} [data.newPatient] - Datos del paciente nuevo
 * @param {string} data.medicalSpecialtyId - ID de la especialidad de urgencia
 * @param {string} data.reason - Motivo de consulta
 * @returns {Promise<Object>} Caso creado
 */
urgencyQueueService.createCase = function (data) {
  return api.post(`${endpoint}/create-case`, data).then((r) => r.data);
};

/**
 * @description Cancela un item de la cola de urgencias
 * @param {string} queueId - ID del item en cola
 * @returns {Promise<Object>} Resultado de la cancelación
 */
urgencyQueueService.cancel = function (queueId) {
  return api.delete(`${endpoint}/${queueId}/cancel`).then((r) => r.data);
};

/**
 * @description Obtiene el historial de la cola para una especialidad (atendidos, cancelados)
 * @param {Object} opts - Opciones de filtro
 * @param {string} opts.specialtyId - ID de la especialidad
 * @param {string} [opts.status] - Status CSV (completed,cancelled,in-progress)
 * @param {string} [opts.dateFrom] - Fecha ISO desde
 * @param {string} [opts.dateTo] - Fecha ISO hasta
 * @param {string} [opts.search] - Texto libre para buscar por nombre/DNI
 * @returns {Promise<Array>} Lista de items históricos
 */
urgencyQueueService.getHistory = function ({
  specialtyId,
  status,
  dateFrom,
  dateTo,
  search,
}) {
  const params = { status: status || "completed,cancelled" };
  if (dateFrom) params.dateFrom = dateFrom;
  if (dateTo) params.dateTo = dateTo;
  if (search) params.search = search;
  return api
    .get(`${endpoint}/specialty/${specialtyId}/history`, { params })
    .then((r) => r.data);
};

export default urgencyQueueService;
